// src/utils/traceabilityEngine.js

import { UNIVERSAL_INDUSTRIES } from '../data/universalIndustries';
import { getAlternativesForComponent } from '../data/componentAlternatives';

// ============================================================
// COMPLETE TRACEABILITY ENGINE
// Connects AutoArchitect → BOM with full justification
// ============================================================

export function generateTraceabilityData(requirements, selectedComponents, approaches, problemTypes, dataTypes) {
  const traceability = {
    requirements: [],
    useCases: [],
    components: [],
    justificationMap: {},
    functionalBOM: [],
    technicalBOM: [],
    confidenceScores: {},
    selectionRationale: {},
    costJustification: {},
    riskAssessment: {}
  };

  // 1. Build Requirement Traceability
  const requirementList = [
    { id: 'req-industry', name: `Industry: ${requirements.industry}`, category: 'Domain' },
    { id: 'req-problems', name: `Problems: ${requirements.problems.join(', ')}`, category: 'Business' },
    { id: 'req-problem-types', name: `Problem Types: ${problemTypes.join(', ')}`, category: 'Technical' },
    { id: 'req-data-types', name: `Data Types: ${dataTypes.join(', ')}`, category: 'Data' },
    { id: 'req-budget', name: `Budget: ${requirements.budget}`, category: 'Financial' },
    { id: 'req-scale', name: `Scale: ${requirements.scale}`, category: 'Operational' },
    { id: 'req-latency', name: `Latency: ${requirements.latency}`, category: 'Performance' },
    { id: 'req-compliance', name: `Compliance: ${requirements.compliance}`, category: 'Governance' }
  ];

  traceability.requirements = requirementList;

  // 2. Build Use Cases from Problems
  const useCases = requirements.problems.map((problemId, index) => {
    return {
      id: `uc-${index + 1}`,
      problemId,
      name: getProblemName(problemId, requirements.industry),
      description: `Use case for solving ${getProblemName(problemId, requirements.industry)}`,
      components: [],
      dataTypes: [],
      stakeholders: [],
      successCriteria: []
    };
  });

  // 3. Map Components to Use Cases and Requirements
  selectedComponents.forEach((comp, compIndex) => {
    const compId = comp.id || comp.componentId || `comp-${compIndex}`;
    const compName = comp.name || 'Unknown Component';
    
    const mappedUseCases = useCases.filter(uc => {
      return doesComponentServeProblem(comp, uc.problemId, requirements.industry);
    });
    
    const mappedRequirements = requirementList.filter(req => {
      return doesComponentSatisfyRequirement(comp, req.id, requirements);
    });

    const justification = generateComponentJustification(comp, requirements, approaches);
    const confidence = calculateConfidenceScore(comp, requirements, mappedUseCases.length);
    const costJustification = generateCostJustification(comp, requirements);
    const risk = assessComponentRisk(comp, requirements);

    const componentEntry = {
      id: compId,
      name: compName,
      layer: comp.layerName || comp.layer || 'Unknown',
      subLayer: comp.subLayerName || comp.subLayer || 'Unknown',
      useCases: mappedUseCases.map(uc => uc.id),
      requirements: mappedRequirements.map(req => req.id),
      justification,
      confidence,
      costJustification,
      risk,
      alternativesCount: getAlternativesForComponent(compName).length,
      selectedApproach: comp.approachId || null
    };

    traceability.components.push(componentEntry);
    traceability.justificationMap[compId] = justification;
    traceability.confidenceScores[compId] = confidence;
    traceability.selectionRationale[compId] = generateSelectionRationale(comp, requirements, approaches);
    traceability.costJustification[compId] = costJustification;
    traceability.riskAssessment[compId] = risk;

    mappedUseCases.forEach(uc => {
      const ucEntry = useCases.find(u => u.id === uc.id);
      if (ucEntry && !ucEntry.components.includes(compId)) {
        ucEntry.components.push(compId);
      }
    });
  });

  traceability.useCases = useCases;
  traceability.functionalBOM = buildFunctionalBOM(traceability.components, useCases);
  traceability.technicalBOM = buildTechnicalBOM(traceability.components);

  return traceability;
}

// ============================================================
// HELPER FUNCTIONS
// ============================================================

function getProblemName(problemId, industryId) {
  const industry = UNIVERSAL_INDUSTRIES[industryId];
  const problem = industry?.problems?.find(p => p.id === problemId);
  return problem?.name || problemId;
}

function doesComponentServeProblem(comp, problemId, industryId) {
  const compName = (comp.name || '').toLowerCase();
  const problemName = getProblemName(problemId, industryId).toLowerCase();
  const layer = (comp.layerName || comp.layer || '').toLowerCase();
  
  const problemKeywords = extractKeywords(problemName);
  return problemKeywords.some(kw => compName.includes(kw) || layer.includes(kw));
}

function extractKeywords(text) {
  const keywords = [];
  const words = text.split(/[\s\-_]+/);
  words.forEach(word => {
    if (word.length > 3) keywords.push(word);
    if (word.includes('classif')) keywords.push('classification', 'model');
    if (word.includes('predict')) keywords.push('prediction', 'regression');
    if (word.includes('detect')) keywords.push('detection', 'anomaly');
    if (word.includes('vision')) keywords.push('vision', 'image');
    if (word.includes('text')) keywords.push('nlp', 'text');
    if (word.includes('chat')) keywords.push('chatbot', 'conversational');
  });
  return [...new Set(keywords)];
}

function doesComponentSatisfyRequirement(comp, reqId, requirements) {
  const compName = (comp.name || '').toLowerCase();
  const layer = (comp.layerName || comp.layer || '').toLowerCase();
  
  switch (reqId) {
    case 'req-industry':
      return true;
    case 'req-problems':
      return true;
    case 'req-problem-types':
      return true;
    case 'req-data-types':
      return layer.includes('data') || layer.includes('storage') || layer.includes('acquisition');
    case 'req-budget':
      return compName.includes('cost') || layer.includes('infrastructure') || layer.includes('compute');
    case 'req-scale':
      return layer.includes('orchestration') || layer.includes('infrastructure') || layer.includes('compute');
    case 'req-latency':
      return layer.includes('execution') || layer.includes('inference') || layer.includes('compute');
    case 'req-compliance':
      return layer.includes('governance') || layer.includes('security') || layer.includes('observability');
    default:
      return false;
  }
}

function generateComponentJustification(comp, requirements, approaches) {
  const layer = comp.layerName || comp.layer || 'layer';
  const reasons = [];
  
  reasons.push(`Selected for ${requirements.industry} industry requirements`);
  
  if (requirements.problems.length > 0) {
    reasons.push(`Supports ${requirements.problems.length} business problem(s)`);
  }
  
  if (requirements.problemTypes?.length > 0) {
    reasons.push(`Aligned with problem types: ${requirements.problemTypes.slice(0, 3).join(', ')}`);
  }
  
  reasons.push(`Essential for ${layer} layer functionality`);
  
  const cost = comp.cost?.high || comp.cost?.low || 0;
  if (requirements.budget === 'low' && cost < 1000) {
    reasons.push('Cost-effective within budget constraints');
  } else if (requirements.budget === 'enterprise' && cost > 10000) {
    reasons.push('Enterprise-grade performance justified');
  }
  
  return reasons.join('. ') + '.';
}

function calculateConfidenceScore(comp, requirements, useCaseCount) {
  let score = 70;
  
  if (useCaseCount > 0) score += 15;
  if (useCaseCount > 2) score += 10;
  
  const altCount = getAlternativesForComponent(comp.name).length;
  if (altCount > 3) score += 5;
  
  if (comp.cost?.low && comp.cost?.high) score += 5;
  
  return Math.min(score, 95);
}

function generateCostJustification(comp, requirements) {
  const low = comp.cost?.low || 0;
  const high = comp.cost?.high || 0;
  
  if (low === 0 && high === 0) {
    return 'Open source / no cost component';
  }
  
  if (requirements.budget === 'low' && high < 500) {
    return `Budget-friendly at $${low}-$${high}`;
  }
  
  if (requirements.budget === 'enterprise' && high > 5000) {
    return `Enterprise investment of $${low}-$${high} justified for scale`;
  }
  
  return `Cost range $${low}-$${high} within budget`;
}

function assessComponentRisk(comp, requirements) {
  const risks = [];
  
  if (comp.cost?.high > 10000) {
    risks.push({ level: 'Medium', description: 'High cost component - budget risk' });
  }
  
  if (requirements.compliance === 'high' && !comp.name?.toLowerCase().includes('hipaa')) {
    risks.push({ level: 'Medium', description: 'Compliance verification needed' });
  }
  
  if (getAlternativesForComponent(comp.name).length === 0) {
    risks.push({ level: 'High', description: 'No alternatives available - vendor lock-in risk' });
  }
  
  if (risks.length === 0) {
    risks.push({ level: 'Low', description: 'Low risk component' });
  }
  
  return risks;
}

function generateSelectionRationale(comp, requirements, approaches) {
  const steps = [];
  
  steps.push(`Industry "${requirements.industry}" selected`);
  steps.push(`Problems identified: ${requirements.problems.length}`);
  steps.push(`Problem types mapped: ${requirements.problemTypes?.length || 0}`);
  steps.push(`Approach selected: ${approaches.length} approaches`);
  steps.push(`Component "${comp.name}" matched via layer "${comp.layerName || comp.layer}"`);
  
  return steps;
}

function buildFunctionalBOM(components, useCases) {
  const functionalBOM = [];
  
  useCases.forEach(uc => {
    const ucComponents = components.filter(c => c.useCases.includes(uc.id));
    functionalBOM.push({
      useCaseId: uc.id,
      useCaseName: uc.name,
      description: uc.description,
      components: ucComponents,
      componentCount: ucComponents.length,
      estimatedCost: calculateUseCaseCost(ucComponents)
    });
  });
  
  return functionalBOM;
}

function buildTechnicalBOM(components) {
  const layers = {};
  
  components.forEach(comp => {
    const layer = comp.layer || 'Unknown';
    if (!layers[layer]) {
      layers[layer] = {
        layerName: layer,
        components: [],
        componentCount: 0,
        estimatedCost: { low: 0, high: 0 }
      };
    }
    layers[layer].components.push(comp);
    layers[layer].componentCount++;
    layers[layer].estimatedCost.low += comp.cost?.low || 0;
    layers[layer].estimatedCost.high += comp.cost?.high || 0;
  });
  
  return Object.values(layers);
}

function calculateUseCaseCost(components) {
  return {
    low: components.reduce((sum, c) => sum + (c.cost?.low || 0), 0),
    high: components.reduce((sum, c) => sum + (c.cost?.high || 0), 0)
  };
}